import { c as _c } from "react/compiler-runtime";
import { AdminTRPCProvider, useFeatureFlag } from '@newfront-insurance/admin-ui';
import { AccountSwitcher } from '@newfront-insurance/admin-ui/accounts';
import { AdminLayoutProvider, AppLayout, AppType } from '@newfront-insurance/admin-ui/layout';
import type { Route } from '@newfront-insurance/next-router-provider';
import { useRouter } from '@newfront-insurance/next-router-provider';
import type { PropsWithChildren, ReactNode, JSX } from 'react';
import { AuthProvider } from './auth';
import { BeamerProvider } from './beamer';
import { SelectedProducerProvider } from './selected-producer-provider';
import { ProducerSelector } from '../components/select-current-producer-modal';
import { useCurrentUserScopes } from '../hooks/use-current-user-scopes';
import { useCanSelectProducer } from '../hooks/use-permission';
import { pageRoutes } from '../routes';
import { useCurrentUserUuid } from '@/client/hooks/use-current-user-uuid';
import { getConfig } from '@/config';
const {
  LINKS
} = getConfig();
export function AdminUIProvider(t0) {
  const $ = _c(10);
  const {
    children,
    isShowingDarkSidebar
  } = t0;
  const appConfig = useAppConfig();
  const accountSwitcherComponent = useAccountSwitcherComponent();
  const producerSelectorComponent = useProducerSelectorComponent();
  const isDarkModeToggleEnabled = useFeatureFlag("dark-mode-toggle", false);
  const t1 = appConfig?.type ?? AppType.ACCOUNTS;
  let t2;
  if ($[0] !== accountSwitcherComponent || $[1] !== appConfig || $[2] !== children || $[3] !== isDarkModeToggleEnabled || $[4] !== isShowingDarkSidebar || $[5] !== producerSelectorComponent) {
    t2 = appConfig ? <AppLayout showDarkModeToggle={isDarkModeToggleEnabled} isShowingDarkSidebar={isShowingDarkSidebar} leftToolbar={accountSwitcherComponent} rightToolbar={<div style={{
      display: "flex",
      gap: 12,
      alignItems: "center"
    }}>{producerSelectorComponent}<BeamerProvider /></div>} homeRoute={appConfig.homeRoute}>{children}</AppLayout> : children;
    $[0] = accountSwitcherComponent;
    $[1] = appConfig;
    $[2] = children;
    $[3] = isDarkModeToggleEnabled;
    $[4] = isShowingDarkSidebar;
    $[5] = producerSelectorComponent;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <AdminTRPCProvider basePath="/api/admin-ui/trpc" authProvider={AuthProvider}><SelectedProducerProvider localStorageKey="current-producer-impersonation"><AdminLayoutProvider apiBasePath="/api/admin-ui/layout" authProvider={AuthProvider} logoutRedirect={LINKS.MARKETING_SITE} legacyAppBaseUrl={LINKS.LEGACY_DASH_APP} currentApp={t1}>{t2}</AdminLayoutProvider></SelectedProducerProvider></AdminTRPCProvider>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
interface AppConfig {
  type: AppType;
  homeRoute: Route;
}
export function useAppConfig() {
  const $ = _c(5);
  const {
    router
  } = useRouter();
  if (router.pathname === "/billing") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        type: AppType.BILLING,
        homeRoute: {
          pathname: "/billing"
        }
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (router.pathname === "/coverage-gap-analysis") {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        type: AppType.COVERAGE_GAP_ANALYSIS,
        homeRoute: {
          pathname: "/coverage-gap-analysis"
        }
      };
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (router.pathname.includes("/coverage-gap-analysis/workflow") || router.pathname.includes("/coverage-gap-analysis/result")) {
    return;
  }
  if (router.pathname.includes("/accounts/[accountUuid]/summary-of-insurance/create")) {
    return;
  }
  const isExposuresRoute = pageRoutes.viewAccountInsurableItemsDetails().pathname === router.pathname;
  if (isExposuresRoute) {
    const t0 = `/accounts/${router.query.accountUuid}/exposures`;
    let t1;
    if ($[2] !== t0) {
      t1 = {
        type: AppType.EXPOSURES,
        homeRoute: {
          pathname: t0
        }
      };
      $[2] = t0;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (router.pathname.endsWith("/policy-change-request")) {
    return;
  }
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: AppType.ACCOUNTS,
      homeRoute: {
        pathname: "/accounts"
      }
    };
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}
export function useAccountSwitcherComponent() {
  const $ = _c(11);
  const {
    getQuery,
    pushRoute,
    router
  } = useRouter();
  let t0;
  if ($[0] !== getQuery) {
    t0 = getQuery("accountUuid");
    $[0] = getQuery;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const accountUuid = t0;
  const {
    currentUserScopes
  } = useCurrentUserScopes();
  const {
    currentUserUuid
  } = useCurrentUserUuid();
  if (!accountUuid) {
    return null;
  }
  let t1;
  if ($[2] !== pushRoute || $[3] !== router.pathname || $[4] !== router.query) {
    t1 = account => pushRoute({
      pathname: router.pathname,
      query: {
        ...router.query,
        accountUuid: account
      }
    });
    $[2] = pushRoute;
    $[3] = router.pathname;
    $[4] = router.query;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== accountUuid || $[7] !== currentUserScopes || $[8] !== currentUserUuid || $[9] !== t1) {
    t2 = <AccountSwitcher loggedInUserUuid={currentUserUuid} currentAccountUuid={accountUuid} userScopes={currentUserScopes} onSelectAccount={t1} />;
    $[6] = accountUuid;
    $[7] = currentUserScopes;
    $[8] = currentUserUuid;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
const PRODUCER_SELECTOR_VISIBLE_PATHNAMES = ['/accounts', '/billing'];
function useProducerSelectorComponent() {
  const $ = _c(1);
  const canSelectProducer = useCanSelectProducer();
  const {
    router
  } = useRouter();
  if (!canSelectProducer || !PRODUCER_SELECTOR_VISIBLE_PATHNAMES.includes(router.pathname)) {
    return null;
  }
  if (canSelectProducer) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <div style={{
        minWidth: 300
      }}><ProducerSelector /></div>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
}